import { useState, useEffect } from "react";
import { Button, Card, Badge, Table, Spin, Typography, Space, message } from "antd";
import { Typography as AntdTypography } from "antd";
import { PlayCircleOutlined, PlusOutlined, ReloadOutlined, ExclamationCircleOutlined, LikeOutlined } from "@ant-design/icons";
import SoporteReemplazoService from "../../services/documentoReemplazo/SoporteReemplazoService";
import { FiltrosLista } from "./FiltrosLista";


const { Title } = Typography;
const { Text } = AntdTypography;
export function ListaSoportes({ onCrearReemplazo, refreshTrigger, limpiar, selectItemRemplazo, onUploaded }) {
  const [soportes, setSoportes] = useState([]);
  const [soportesFiltrados, setSoportesFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");

  useEffect(() => {
    cargarSoportes();
  }, [refreshTrigger]);

  useEffect(() => {
    aplicarFiltros();
  }, [soportes, filtroEstado, filtroTexto]);

  const cargarSoportes = async () => {
    setLoading(true);
    try {
      const data = await SoporteReemplazoService.list(selectItemRemplazo);
      setSoportes(data); 
    } catch (error) {
      console.error("Error al cargar soportes", error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let filtrados = [...soportes];

    if (filtroEstado) {
      filtrados = filtrados.filter(
        (soporte) => soporte.estadoReemplazo?.toString() === filtroEstado
      );
    }

    if (filtroTexto) {
      filtrados = filtrados.filter(
        (soporte) =>
          soporte.nombre_soporte
            ?.toLowerCase()
            .includes(filtroTexto.toLowerCase()) ||
          soporte.codUnico
            ?.toLowerCase()
            .includes(filtroTexto.toLowerCase())
      );
    }

    setSoportesFiltrados(filtrados);
  };

  const limpiarFiltros = () => {
    setFiltroEstado("");
    setFiltroTexto("");
  };

  const procesarSoporte = async (codUnico, id) => {
    setProcessingId(id);
    try {
      const resp = await SoporteReemplazoService.processByCodUnico(codUnico);

      if (resp.success) {
        message.success(resp.message || "Reemplazo realizado correctamente");
      } else {
        message.error(resp.message || "No se realizó el reemplazo");
      }

      cargarSoportes();
      limpiar();
      // ✅ Notifica a FolderCard que hubo un cambio para refrescar el grid externo
      onUploaded?.();
    } catch (error) {
      console.error("Error al procesar soporte", error);
      message.error("❌ Error inesperado al procesar soporte");
    } finally {
      setProcessingId(null);
    }
  };

  const procesarTodos = async () => {
    setProcessingAll(true);
    try {
      const resp = await SoporteReemplazoService.processAll();

      if (resp.success) {
        message.success(resp.message || "Todos los soportes fueron procesados correctamente");
      }
      cargarSoportes();
      limpiar();
      // ✅ Notifica a FolderCard que hubo un cambio para refrescar el grid externo
      onUploaded?.();
    } catch (error) {
      console.error("Error al procesar todos", error);
      message.error("❌ Error inesperado al procesar todos los soportes");
    } finally {
      setProcessingAll(false);
    }
  };

  const columns = [
    {
      title: "Código",
      dataIndex: "codUnico",
      width: 100,
      align: "center",
      key: "codUnico",
      render: (text) => <span style={{ fontFamily: "monospace" }}>{text}</span>,
    },
    {
      title: "Nombre Soporte (nuevo)",
      dataIndex: "nombre_soporte",
      width: 150,
      key: "nombre_soporte",
      ellipsis: true,
    },
    {
      title: "Nombre Soporte (antiguo)",
      dataIndex: "nombre_soporte_old",
      width: 150,
      ellipsis: true,
      render: (text) => text || <span style={{ color: "#999" }}>Sin historial</span>,
    },
    {
      title: "Ruta Soporte",
      dataIndex: "ruta_old",
      key: "ruta_old",
      width: 1,
      render: (text) => {
        if (!text) return <span style={{ color: "#999" }}>Sin ruta</span>;

        const rutaLimpia = text.replace(
          /DocumentosPOA\/actividad_\d+\/tarea_\d+\/indicador_\d+\/medio_verificacion_\d+\//,
          ""
        );

        return rutaLimpia;
      },
    },
    {
      title: "Estado",
      dataIndex: "estadoReemplazoTexto",
      key: "estado",
      width: 90,
      align: "center",
      render: (text, record) => {
        const colorMap = {
          1: "gold",
          2: "green",
          3: "red",
          4: "blue",
          5: "red",
          0: "red",
        };
        return <Badge color={colorMap[record.estadoReemplazo]} text={text} />;
      },
    },
    {
      title: "Fecha Registro",
      dataIndex: "fecha_reemplazo",
      key: "fecha",
      width: 90,
      align: "center",
      render: (fecha) =>
        fecha && fecha !== "0000-00-00"
          ? new Date(fecha).toLocaleDateString()
          : "Sin procesar",
    },
    {
      title: "Fecha Reemplazo",
      dataIndex: "fecha_procesado",
      key: "fecha",
      width: 90,
      align: "center",
      render: (fecha) =>
        fecha && fecha !== "0000-00-00"
          ? new Date(fecha).toLocaleDateString()
          : "Sin procesar",
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 50,
      align: "center",
      render: (_, record) => {
        if (record.estadoReemplazo === 1) {
          // Pendiente → mostrar botón procesar
          return (
            <Button
              size="small"
              icon={processingId === record.id ? <ReloadOutlined spin /> : <ExclamationCircleOutlined style={{ color: "#FFA500" }} />
              }
              onClick={() => procesarSoporte(record.codUnico, record.id)}
              disabled={processingId === record.id}
            >
              Procesar
            </Button>
          );
        } else {
          // Ya procesado → mostrar texto o etiqueta
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <LikeOutlined style={{ color: "green" }} /> Procesado
            </Text>
          );
        }
      },
    }

  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* Filtros */}
      <FiltrosLista
        filtroEstado={filtroEstado}
        filtroTexto={filtroTexto}
        onFiltroEstadoChange={setFiltroEstado}
        onFiltroTextoChange={setFiltroTexto}
        onLimpiarFiltros={limpiarFiltros}
      />

      {/* Lista de soportes */}
      <Card
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Lista de Soportes a Reemplazar
            </Title>
            <Badge count={soportesFiltrados.length} />
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCrearReemplazo}
            >
              Crear Reemplazo
            </Button>
            <Button
              icon={
                processingAll ? <ReloadOutlined spin /> : <PlayCircleOutlined />
              }
              onClick={procesarTodos}
              disabled={processingAll}
            >
              Procesar Todos
            </Button>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={soportesFiltrados}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: "max-content" }}
          />
        )}
      </Card>
    </Space>
  );
}